import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, BedDouble, RotateCw, Sparkles, Stethoscope, Wallet } from 'lucide-react';
import { SOURCE_MESSAGES, type DashboardOverviewDto, type DashboardSoFar, type DoctorOverviewDto } from '@his/shared';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { tr } from '@/lib/i18n-core';
import { cn, display, moneyCompact, relative } from '@/lib/utils';
import { visibleSections } from '@/lib/navigation';
import { localiseSections } from '@/lib/navigation-i18n';
import { Alert, Button, Tooltip } from '@/components/ui/primitives';
import { changePct, hourInZone, partOfDay } from './dashboard-math';
import { HospitalOverview } from './HospitalOverview';
import { DoctorOverview } from './DoctorOverview';
import { UnpricedOrdersAlert } from '../billing/UnpricedOrdersAlert';

const REFRESH_MS = 120_000;

interface HeroLine {
  key: string;
  node: ReactNode;
}

function whileVisible(): number | false {
  return document.visibilityState === 'visible' ? REFRESH_MS : false;
}

/**
 * Role-based dashboards (SRS 10.4). Which tiles appear follows the signed-in
 * user's permissions, so a doctor lands on their own workload and an
 * administrator lands on the hospital's.
 */
export function DashboardPage() {
  const { user, can, hasModule, worksIn } = useAuth();

  const seesHospital = can('report.read.all', 'report.financial');
  const isDoctor = Boolean(user?.roles.some((r) => ['doctor', 'medical_officer'].includes(r.code)));

  const hospital = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => get<DashboardOverviewDto>('/reports/dashboard/overview'),
    enabled: seesHospital && hasModule('reports'),
    refetchInterval: whileVisible,
  });

  const doctor = useQuery({
    queryKey: ['dashboard', 'doctor-overview'],
    queryFn: () => get<DoctorOverviewDto>('/reports/dashboard/doctor/overview'),
    enabled: isDoctor && hasModule('reports'),
    refetchInterval: whileVisible,
  });

  const timeZone = hospital.data?.timezone ?? display.timezone;
  const currentHour = hourInZone(timeZone);
  const title = greeting(user?.displayName, currentHour);

  if (!hasModule('reports')) {
    // A hospital without Reports used to land on nothing but an apology. The
    // KPI tiles genuinely need the reporting module, but the way in to the work
    // does not — so the screen offers the modules this place actually has.
    return (
      <>
        <DashboardHero
          title={title}
          timeZone={timeZone}
          lines={[
            {
              key: 'intro',
              node: tr('admin', 'dashboard_intro_modules'),
            },
          ]}
        />
        <UnpricedOrdersAlert className="mt-4" />
        <QuickLinks />
        <p className="mt-6 text-sm text-muted-foreground">{tr('admin', 'dashboard_reports_not_in_plan')}</p>
      </>
    );
  }

  const lines: HeroLine[] = [];
  if (hospital.data) {
    const data = hospital.data;
    if (worksIn('opd'))
      lines.push({
        key: 'opd',
        node: (
          <Insight icon={Stethoscope}>{opdPace(data.today.opdVisits)}</Insight>
        ),
      });
    if (worksIn('ipd') && data.beds.total) {
      lines.push({
        key: 'ipd',
        node: (
          <Insight icon={BedDouble}>
            {tr('admin', 'dashboard_beds_full_free', {
              pct: Math.round((data.beds.occupied / data.beds.total) * 100),
              free: data.beds.total - data.beds.occupied,
            })}
          </Insight>
        ),
      });
    }
    if (worksIn('billing')) {
      lines.push({
        key: 'billing',
        node: (
          <Insight icon={Wallet}>
            {tr('admin', 'dashboard_collected_so_far', { amount: moneyCompact(data.today.collectedMinor.today) })}
          </Insight>
        ),
      });
    }
  } else if (doctor.data) {
    const waiting = doctor.data.appointments.find((row) => row.key === 'arrived')?.count ?? 0;
    lines.push({
      key: 'doctor',
      node: (
        <Insight icon={Stethoscope}>
          {waiting
            ? tr('admin', 'dashboard_patients_waiting_for_you', { count: waiting })
            : tr('admin', 'dashboard_nobody_waiting')}
        </Insight>
      ),
    });
  }

  const refreshing = hospital.isFetching || doctor.isFetching;
  const computedAt = hospital.data?.computedAt ?? doctor.data?.computedAt;
  const hasDashboard =
    (seesHospital || isDoctor) &&
    (hospital.isLoading || doctor.isLoading || Boolean(hospital.data || doctor.data));

  return (
    <div className="space-y-6">
      <DashboardHero
        title={title}
        timeZone={timeZone}
        lines={
          lines.length
            ? lines
            : [
                {
                  key: 'intro',
                  node: tr('admin', hasDashboard ? 'dashboard_pulling_together' : 'dashboard_where_you_left_off'),
                },
              ]
        }
        action={
          hasDashboard && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {computedAt && (
                <span className="hidden sm:inline">{tr('admin', 'dashboard_updated', { when: relative(computedAt) })}</span>
              )}
              <Tooltip content={tr('admin', 'dashboard_refresh')}>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 bg-card/70 backdrop-blur"
                  aria-label={tr('admin', 'dashboard_refresh')}
                  disabled={refreshing}
                  onClick={() => {
                    if (seesHospital) void hospital.refetch();
                    if (isDoctor) void doctor.refetch();
                  }}
                >
                  <RotateCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
                </Button>
              </Tooltip>
            </div>
          )
        }
      />

      <UnpricedOrdersAlert />

      {seesHospital && (
        <section aria-label={tr('admin', 'dashboard_hospital_today')}>
          {hospital.isLoading && <DashboardSkeleton />}
          {hospital.isError && <Alert variant="destructive">{tr('admin', 'dashboard_load_failed')}</Alert>}
          {hospital.data && <HospitalOverview data={hospital.data} currentHour={currentHour} />}
        </section>
      )}

      {isDoctor && (
        <section aria-label={tr('admin', 'dashboard_my_day')} className="space-y-3">
          {seesHospital && <SectionTitle>{tr('admin', 'dashboard_my_day')}</SectionTitle>}
          {doctor.isLoading && <DashboardSkeleton compact />}
          {doctor.data && <DoctorOverview data={doctor.data} />}
        </section>
      )}

      {user?.roleModules && (
        <section aria-label={tr('admin', 'dashboard_jump_back_in')} className="space-y-3">
          {hasDashboard && <SectionTitle>{tr('admin', 'dashboard_jump_back_in')}</SectionTitle>}
          <QuickLinks />
        </section>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
      <span className="h-px flex-1 bg-border" aria-hidden />
    </h2>
  );
}

function Insight({ icon: Icon, children }: { icon: typeof Stethoscope; children: ReactNode }) {
  return (
    <span className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span>{children}</span>
    </span>
  );
}

function DashboardHero({
  title,
  timeZone,
  lines,
  action,
}: {
  title: string;
  timeZone: string;
  lines: HeroLine[];
  action?: ReactNode;
}) {
  const today = new Intl.DateTimeFormat(display.locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date());
  return (
    <header className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/15 via-primary/5 to-card p-5 shadow-sm duration-500 animate-in fade-in-0 slide-in-from-top-2 sm:p-6">
      <svg
        aria-hidden
        viewBox="0 0 600 120"
        preserveAspectRatio="none"
        className="pointer-events-none absolute bottom-0 end-0 h-20 w-3/4 text-primary opacity-20 [mask-image:linear-gradient(to_right,transparent,black_45%)] sm:h-24 sm:w-1/2 rtl:-scale-x-100"
      >
        <path
          d="M0 80 H170 L182 80 L192 52 L204 104 L216 22 L228 96 L240 80 H360 L372 80 L382 58 L394 100 L406 34 L418 90 L430 80 H600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray="1400"
          className="animate-draw-line"
        />
      </svg>
      <span
        aria-hidden
        className="pointer-events-none absolute -end-16 -top-20 h-56 w-56 rounded-full bg-primary/20 blur-3xl"
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-primary">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {today}
          </p>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
        </div>
        {action}
      </div>
      <div className="relative mt-3 flex max-w-3xl flex-col gap-1.5 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6">
        {lines.map((line, index) => (
          <span
            key={line.key}
            className="duration-500 animate-in fade-in-0 fill-mode-backwards"
            style={{ animationDelay: `${150 + index * 120}ms` }}
          >
            {line.node}
          </span>
        ))}
      </div>
    </header>
  );
}

function DashboardSkeleton({ compact }: { compact?: boolean }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label={tr('admin', 'dashboard_loading')}>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-[9.5rem] animate-pulse rounded-xl border bg-muted/40" />
        ))}
      </div>
      {!compact && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="h-80 animate-pulse rounded-xl border bg-muted/40 lg:col-span-2" />
          <div className="h-80 animate-pulse rounded-xl border bg-muted/40" />
        </div>
      )}
    </div>
  );
}

/**
 * Titles come first in this part of the world, so greeting people by the first
 * word of their name greeted half the hospital as "Dr".
 */
const TITLES = new Set([
  'dr',
  'dr.',
  'prof',
  'prof.',
  'mr',
  'mr.',
  'mrs',
  'mrs.',
  'ms',
  'ms.',
  'miss',
  'sr',
  'sr.',
  'hakeem',
  'hafiz',
  'mufti',
  'syed',
  'mian',
  'ch',
  'ch.',
]);

function greeting(displayName: string | undefined, hour: number): string {
  const part = partOfDay(hour);
  const words = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return tr('admin', `dashboard_good_${part}`);
  // "Dr Imran Sheikh" → "Dr Imran"; "Imran Sheikh" → "Imran".
  const familiar =
    TITLES.has(words[0].toLowerCase()) && words.length > 1 ? `${words[0]} ${words[1]}` : words[0];
  return tr('admin', `dashboard_good_${part}_name`, { name: familiar });
}

function opdPace(soFar: DashboardSoFar): string {
  if (soFar.today === 0 && soFar.lastWeek === 0) return tr('admin', 'dashboard_pace_none');
  const visits = tr('admin', 'dashboard_opd_visit_count', { count: soFar.today });
  const change = changePct(soFar);
  if (change === null) return tr('admin', 'dashboard_pace_today', { visits });
  if (Math.abs(change) < 5) return tr('admin', 'dashboard_pace_same', { visits });
  return tr('admin', change > 0 ? 'dashboard_pace_busier' : 'dashboard_pace_quieter', { visits, pct: Math.abs(change) });
}

/** The modules this hospital actually has, as a way in rather than a menu hunt. */
function QuickLinks() {
  const { can, worksIn } = useAuth();
  const shellText = useTranslation('shell');
  const ownSections = visibleSections(can, worksIn).filter(
    (section) => section.title !== 'Your account',
  );
  const sections =
    shellText.translated && shellText.locale !== 'en'
      ? localiseSections(ownSections, (key) => (key in SOURCE_MESSAGES.shell ? shellText.t(key) : null))
      : ownSections;

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {sections.map((section, index) => (
        <div
          key={section.title}
          className="rounded-xl border bg-card p-4 shadow-sm duration-500 animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-backwards"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <p className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {section.title}
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] tabular-nums">
              {section.items.length}
            </span>
          </p>
          <ul className="grid gap-1">
            {section.items.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-ring"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <item.icon className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <ArrowRight
                    className="h-3.5 w-3.5 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100 rtl:rotate-180"
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
